import { Link } from 'react-router-dom';
import { PurchaseDocumentDetail } from '../../../../app/models/PurchaseDocumentDetail';
import ButtonTableAction from '../../../../components/buttons/ButtonTableAction';
import DefaultDataTable from '../../../../components/default/DefaultDataTable';
import { PurchaseDocumentDetailFilters } from '../../../../app/models/PurchaseDocumentDetail';
import { PaymentStatusDataForm, PaymentStatusesStatus } from '../../../../app/models/PaymentStatus';

interface Props {
    purchaseDocumentDetail: PaymentStatusDataForm[];
    totalRows: number;
    loading?: boolean;
    filter: PurchaseDocumentDetailFilters;
    setFilter: (filter: PurchaseDocumentDetailFilters) => void;
    edit?: (id: number) => void;
    destroy?: (id: number) => void;
    show?: (id: number) => void;
    callbackSuccess: () => void;
    statuses: PaymentStatusesStatus[];
}

const PurchaseDocumentDetailDataTable = ({
    purchaseDocumentDetail = [],
    totalRows,
    loading = false,
    filter,
    setFilter,
    edit,
    destroy,
    show,
    callbackSuccess,
    statuses,
}: Props) => {
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    const columns = [
        {
            name: 'Pos',
            selector: (row: any) => row.position,
            sortable: true,
            sortField: 'position',
            width: '80px',
            cell: (row: any) => <span>{row.position}</span>,
        },
        {
            name: 'Hoja de Entrada',
            selector: (row: any) => row.hoja_entrada || row.service_manifest_id,
            sortable: true,
            sortField: 'hoja_entrada',
            width: '140px',
            cell: (row: any) => {
                const manifestId = row.service_manifest_id != null && row.service_manifest_id !== '' ? row.service_manifest_id : null;
                const label = (row.hoja_entrada != null && row.hoja_entrada !== '' && String(row.hoja_entrada) !== 'null')
                    ? String(row.hoja_entrada)
                    : (manifestId != null ? `SM-${String(manifestId).padStart(5, '0')}` : null);
                if (manifestId != null && label) {
                    return (
                        <Link
                            to={`/service-manifests/${manifestId}/show`}
                            className="text-primary text-decoration-underline"
                        >
                            {label}
                        </Link>
                    );
                }
                return <span className="text-muted">-</span>;
            },
        },
        {
            name: 'Glosa',
            selector: (row: any) => row.product_purchase_name || row.item,
            sortable: true,
            sortField: 'product_purchase_name',
            cell: (row: any) => (
                <span><strong>{row.product_purchase_name || row.item || `Detalle #${row.id}`}</strong></span>
            ),
        },
        {
            name: 'Cantidad',
            selector: (row: any) => row.cant,
            sortable: true,
            sortField: 'cant',
            width: '120px',
            cell: (row: any) => (
                <span>{row.cant != null ? `${row.cant} ${row.unit || ''}` : '-'}</span>
            ),
        },
        {
            name: 'Precio un',
            selector: (row: any) => row.total_unit,
            sortable: true,
            sortField: 'total_unit',
            width: '130px',
            cell: (row: any) => (
                <span style={{ display: 'block', textAlign: 'right', width: '100%' }}>{formatCurrency(Number(row.total_unit) || 0)}</span>
            ),
        },
        {
            name: 'Valor Neto',
            selector: (row: any) => row.total_neto,
            sortable: true,
            sortField: 'total_neto',
            width: '130px',
            cell: (row: any) => (
                <span style={{ display: 'block', textAlign: 'right', width: '100%' }}>{formatCurrency(Number(row.total_neto) || 0)}</span>
            ),
        },
        {
            name: 'Impuestos',
            selector: (row: any) => row.total_tax,
            sortable: true,
            sortField: 'total_tax',
            width: '130px',
            cell: (row: any) => (
                <span style={{ display: 'block', textAlign: 'right', width: '100%' }}>{formatCurrency(Number(row.total_tax) || 0)}</span>
            ),
        },
        {
            name: 'Total',
            selector: (row: any) => row.total,
            sortable: true,
            sortField: 'total',
            width: '150px',
            cell: (row: any) => (
                <span style={{ display: 'block', textAlign: 'right', width: '100%' }}><strong>{formatCurrency(Number(row.total) || 0)}</strong></span>
            ),
        },
        {
            name: 'Acciones',
            selector: (row: any) => row.id,
            sortable: false,
            width: '140px',
            cell: (row: any) => (
                <div>
                    {show && (
                        <ButtonTableAction
                            callbackFunction={() => show(row.id ?? -1)}
                            classIcon="mdi mdi-eye"
                            colorIcon="text-info"
                            errorMessage="No se puede mostrar este registro."
                            title="Ver"
                        />
                    )}
                    {edit && (
                        <ButtonTableAction
                            callbackFunction={() => edit(row.id ?? -1)}
                            classIcon="mdi mdi-pencil"
                            colorIcon="text-warning"
                            errorMessage="No se puede editar este registro."
                            title="Editar"
                        />
                    )}
                    {destroy && (
                        <ButtonTableAction
                            callbackFunction={() => destroy(row.id ?? -1)}
                            classIcon="mdi mdi-delete"
                            colorIcon="text-danger"
                            errorMessage="No se puede eliminar este registro."
                            title="Eliminar"
                        />
                    )}
                </div>
            ),
        },
    ];

    return (
        <DefaultDataTable
            columns={columns}
            data={purchaseDocumentDetail}
            progressPending={loading}
            paginationTotalRows={totalRows}
            filter={filter}
            setFilter={setFilter}
        />
    );
};

export default PurchaseDocumentDetailDataTable;
